<template>
  <div class="ds-data-table">
    <!-- 加载态 -->
    <div v-if="loading" class="ds-data-table__loading" v-loading="true" element-loading-text="加载中"></div>

    <!-- 错误态 -->
    <ds-error-state
      v-else-if="error"
      :title="errorTitle"
      :description="errorMessage"
      :code="errorCode"
      @retry="$emit('retry')">
    </ds-error-state>

    <!-- 空态 -->
    <ds-empty-state
      v-else-if="isEmpty"
      :type="emptyType"
      :description="emptyDescription">
      <template v-if="$slots.empty" slot="action">
        <slot name="empty"></slot>
      </template>
    </ds-empty-state>

    <!-- 数据区：使用 el-table 保持与现有页面完全一致的列绑定方式 -->
    <el-table
      v-else
      ref="table"
      v-bind="$attrs"
      v-on="$listeners"
      :data="data"
      :row-key="rowKey"
      :border="border"
      :stripe="stripe"
      :height="height"
      :max-height="maxHeight"
      :size="tableSize"
      :header-cell-style="headerCellStyle"
      :cell-style="cellStyle"
      style="width: 100%">
      <slot></slot>
    </el-table>
  </div>
</template>

<script>
import DsErrorState from './ds-error-state.vue'
import DsEmptyState from './ds-empty-state.vue'

/**
 * DsDataTable —— 表格外壳
 *
 * 职责：统一加载态 / 空态 / 错误态 / 行高密度 / 表头样式。
 * **表格列完全由使用方通过 slot 提供**，因此列顺序、prop 绑定、分页行为
 * 与改造前完全一致，迁移不会改变数据呈现逻辑。
 *
 * Props:
 *  - data             {Array}   表格数据
 *  - loading          {Boolean} 加载态
 *  - error            {Boolean} 错误态
 *  - errorTitle       {String}  错误标题
 *  - errorMessage     {String}  错误描述
 *  - errorCode        {String}  错误码
 *  - emptyType        {String}  空态类型
 *  - emptyDescription {String}  空态描述
 *  - rowKey           {String}  行主键字段
 *  - density          {String}  'default'(44px) | 'compact'(36px)
 *  - border           {Boolean} 是否显示边框
 *  - stripe           {Boolean} 是否斑马纹
 *  - height           {String|Number} 固定高度
 *  - maxHeight        {String|Number} 最大高度
 *
 * Events:
 *  - retry  错误态点击重试
 *
 * 注意：透传 $attrs / $listeners，保证 el-table 原有事件（如 sort-change、
 * selection-change）不丢失。
 */
export default {
  name: 'DsDataTable',
  components: { DsErrorState, DsEmptyState },
  inheritAttrs: false,
  props: {
    data: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    errorTitle: {
      type: String,
      default: ''
    },
    errorMessage: {
      type: String,
      default: ''
    },
    errorCode: {
      type: [String, Number],
      default: ''
    },
    emptyType: {
      type: String,
      default: 'no-data'
    },
    emptyDescription: {
      type: String,
      default: ''
    },
    rowKey: {
      type: String,
      default: ''
    },
    density: {
      type: String,
      default: 'default'
    },
    border: {
      type: Boolean,
      default: false
    },
    stripe: {
      type: Boolean,
      default: false
    },
    height: {
      type: [String, Number],
      default: undefined
    },
    maxHeight: {
      type: [String, Number],
      default: undefined
    }
  },
  computed: {
    isEmpty () {
      return !this.loading && !this.error && (!this.data || this.data.length === 0)
    },
    tableSize () {
      return 'mini'
    },
    headerCellStyle () {
      return {
        backgroundColor: 'var(--ds-gray-100)',
        color: 'var(--ds-gray-700)',
        fontWeight: '600',
        padding: this.density === 'compact' ? '7px 0' : '10px 0'
      }
    },
    cellStyle () {
      return {
        padding: this.density === 'compact' ? '7px 0' : '10px 0'
      }
    }
  },
  methods: {
    /**
     * 暴露 el-table 实例方法，便于父组件调用（如 doLayout / clearSelection）
     */
    getTableInstance () {
      return this.$refs.table
    },
    toggleRowSelection (row, selected) {
      const table = this.getTableInstance()
      if (table && table.toggleRowSelection) {
        table.toggleRowSelection(row, selected)
      }
    },
    toggleAllSelection () {
      const table = this.getTableInstance()
      if (table && table.toggleAllSelection) {
        table.toggleAllSelection()
      }
    },
    clearSelection () {
      const table = this.getTableInstance()
      if (table && table.clearSelection) {
        table.clearSelection()
      }
    },
    setCurrentRow (row) {
      const table = this.getTableInstance()
      if (table && table.setCurrentRow) {
        table.setCurrentRow(row)
      }
    },
    doLayout () {
      const table = this.getTableInstance()
      if (table && table.doLayout) {
        table.doLayout()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ds-data-table {
  position: relative;
  background: #fff;

  &__loading {
    min-height: 160px;
  }
}
</style>
